/**
 * PSP/CRE Tool v2.0 - Medical Rules of Eligibility (MedROE, called MRoE in the code) Configuration
 * 2026-10-21: MedROE = medical rules of ELIGIBILITY (who may receive what care at the military facilities); LLE = life, limb or eyesight emergency (docs/research/RESEARCH_MEDROE.md)
 * EPW (Enemy Prisoners of War) and Civilian Casualty Planning
 *
 * Purpose: Define planning factors for EPW medical support and civilian
 * casualty responsibilities that affect medical planning.
 *
 * Author: Dr. Tatar Radu MD
 * Date: December 21, 2024
 * Version: 4.0 - V4-CAL-001 (April 5, 2026): Two-stream patient load.
 *   Added: CIVILIAN_TRIAGE_PROFILES, EPW_BED_MULT (exported), _CIV_DENSITY_TO_TRIAGE (internal).
 *   getMRoEModifiers() return extended: epw_stream{}, civilian_stream{} objects.
 *   epw_stream: daily_patients, section_beds_daily, collection_hold_days, mp_escort_required,
 *     dedicated_section_required, additive=true (does NOT compete with military surgical).
 *   civilian_stream: daily_patients, triage_profile (URBAN/RURAL), t1_surgical_daily,
 *     t2_ward_daily, t3_hns_daily, competes_with_military, civ_active_fraction.
 *   All V3.1 / V2.x output fields retained (backward compat).
 * Version: 3.0 - V3.1 (April 4, 2026): Complete redesign to NATO/ISAF MedROE structure.
 *   Added: MROE_STATES, PATIENT_CATEGORIES, MROE_DEFAULT_MATRICES, MROE_Y_STAR_CONDITIONS,
 *   MROE_PRESETS. Updated: getMRoEModifiers (matrix-aware, backward compat), DEFAULT_MROE_CONFIG.
 *   All pre-V3.1 exports retained for backward compatibility.
 *   Prior: v2.1 - ENH-019 (March 13, 2026): validateMRoEConfig returns `valid` field.
 *
 * References:
 * - Geneva Convention III (Treatment of Prisoners of War) Art. 12, 19, 20
 * - Geneva Convention IV (Protection of Civilian Persons) Art. 16
 * - NATO AJMedP-1 Allied Joint Medical Planning Doctrine
 * - FM 4-02.46 Medical Support to Detainee Operations
 * - ISAF SOP 028.01 Medical Rules of Engagement (2009)
 *
 * Usage:
 * import {
 *   MROE_STATES, PATIENT_CATEGORIES, MROE_DEFAULT_MATRICES,
 *   MROE_Y_STAR_CONDITIONS, MROE_PRESETS,
 *   EPW_THREAT_LEVELS, CIVILIAN_CASUALTY_LEVELS,
 *   getMRoEModifiers, DEFAULT_MROE_CONFIG
 * } from '../constants/mroeConfig';
 */

import { STATUS_ICONS, MEDICAL_ICONS } from '../utils/icons';

// ============================================================================
// V3.1 -- MRoE STATE (GREEN / AMBER / RED)
// Represents the theater command MedROE state from the OPORD.
// ============================================================================

export const MROE_STATES = {
  GREEN: {
    value:       'GREEN',
    label:       'GREEN',
    color:       'success',
    description: 'Full treatment obligations. All categories treated per standard MedROE template.',
  },
  AMBER: {
    value:       'AMBER',
    label:       'AMBER',
    color:       'warning',
    description: 'Selective obligations. HN unpartnered and Contractor treatment conditional on resources.',
  },
  RED: {
    value:       'RED',
    label:       'RED',
    color:       'error',
    description: 'Minimal obligations. Emergency stabilisation only for non-military categories.',
  },
};

// ============================================================================
// V3.1 -- PATIENT CATEGORIES (8 rows of the treatment matrix)
// ============================================================================

/**
 * lockTier values:
 *   'A'      = absolutely locked (tool refuses N, no override possible)
 *   'B'      = hard warning + required acknowledgement before N allowed
 *   'B_soft' = soft warning + single confirmation before N allowed
 *   null     = freely editable
 */
export const PATIENT_CATEGORIES = [
  {
    key:      'COALITION_MILITARY',
    label:    'Coalition Military',
    lockTier: 'A',
    gcRef:    null,
    lockMsg:  'Coalition military forces must always receive treatment. Cannot be modified.',
  },
  {
    key:      'HN_PARTNERED',
    label:    'HN Forces (partnered)',
    lockTier: null,
    gcRef:    null,
    lockMsg:  null,
  },
  {
    key:      'HN_UNPARTNERED',
    label:    'HN Forces (unpartnered)',
    lockTier: null,
    gcRef:    null,
    lockMsg:  null,
  },
  {
    key:      'CIVCAS_OWN',
    label:    'CIVCAS (own forces)',
    lockTier: 'B',
    gcRef:    'GC IV Art.16',
    lockMsg:
      'Setting LLE to N means forces will not treat life-threatening casualties caused by own weapons. ' +
      'GC IV Art.16 violation risk. OPORD authorization + LEGAD sign-off required. ' +
      'This modification will be printed in all generated reports.',
  },
  {
    key:      'CIVCAS_OTHER',
    label:    'CIVCAS (other)',
    lockTier: 'B_soft',
    gcRef:    'GC IV',
    lockMsg:
      'Setting this cell to N excludes civilian casualties from treatment authorization. ' +
      'Under MRoE GREEN this is a significant deviation. LEGAD review recommended.',
  },
  {
    key:      'EPW_EWIA',
    label:    'EWIA / EPW',
    lockTier: 'A',
    gcRef:    'GC III Art.12',
    lockMsg:
      'GC III Art.12 -- Enemy wounded must receive treatment at the same standard as own forces. ' +
      'This obligation cannot be modified.',
  },
  {
    key:      'VIP_HVT',
    label:    'VIP / HVT',
    lockTier: null,
    gcRef:    null,
    lockMsg:  null,
  },
  {
    key:      'CONTRACTORS',
    label:    'Contractors',
    lockTier: null,
    gcRef:    null,
    lockMsg:  null,
  },
];

// ============================================================================
// V3.1 -- Y* CONDITION CHECKLIST
// ============================================================================

/**
 * Conditions documented when a cell is set to Y* (conditional treatment).
 * Stored as array of keys in cell.lleConds / cell.nonLleConds.
 */
export const MROE_Y_STAR_CONDITIONS = [
  { key: 'COMMANDER_APPROVAL',   label: 'Commander approval required' },
  { key: 'LEGAD_CLEARANCE',      label: 'LEGAD clearance required' },
  { key: 'RESOURCES_PERMITTING', label: 'Resources permitting (MASCAL triage applies)' },
  { key: 'SOFA_PENDING',         label: 'SOFA verification pending' },
  { key: 'OTHER',                label: 'Other (specify below)' },
];

// ============================================================================
// V3.1 -- DEFAULT MATRICES PER STATE
// ============================================================================

/**
 * Cell structure:
 * {
 *   lle:         'Y'|'Y*'|'N'  -- LLE: life, limb or eyesight emergency (the surgical cases)
 *   lleConds:    string[]       -- MROE_Y_STAR_CONDITIONS keys if lle === 'Y*'
 *   lleCondNote: string         -- Free-text condition note
 *   nonLle:         'Y'|'Y*'|'N'  -- Non-life-threatening (urgent / routine)
 *   nonLleConds:    string[]
 *   nonLleCondNote: string
 * }
 */
const _mkCell = (lle, nonLle) => ({
  lle:            lle,
  lleConds:       [],
  lleCondNote:    '',
  nonLle:         nonLle,
  nonLleConds:    [],
  nonLleCondNote: '',
});

export const MROE_DEFAULT_MATRICES = {
  GREEN: {
    COALITION_MILITARY: _mkCell('Y',  'Y'),
    HN_PARTNERED:       _mkCell('Y',  'Y'),
    HN_UNPARTNERED:     _mkCell('Y',  'Y*'),
    CIVCAS_OWN:         _mkCell('Y',  'Y'),
    CIVCAS_OTHER:       _mkCell('Y',  'Y*'),
    EPW_EWIA:           _mkCell('Y',  'Y'),
    VIP_HVT:            _mkCell('Y*', 'Y*'),
    CONTRACTORS:        _mkCell('Y*', 'Y*'),
  },
  AMBER: {
    COALITION_MILITARY: _mkCell('Y',  'Y'),
    HN_PARTNERED:       _mkCell('Y',  'Y*'),
    HN_UNPARTNERED:     _mkCell('Y*', 'N'),
    CIVCAS_OWN:         _mkCell('Y',  'Y'),
    CIVCAS_OTHER:       _mkCell('Y*', 'N'),
    EPW_EWIA:           _mkCell('Y',  'Y'),
    VIP_HVT:            _mkCell('Y*', 'N'),
    CONTRACTORS:        _mkCell('N',  'N'),
  },
  RED: {
    COALITION_MILITARY: _mkCell('Y',  'Y'),
    HN_PARTNERED:       _mkCell('Y*', 'N'),
    HN_UNPARTNERED:     _mkCell('N',  'N'),
    CIVCAS_OWN:         _mkCell('Y',  'Y*'),
    CIVCAS_OTHER:       _mkCell('N',  'N'),   // 2026-10-21: red = minimal obligations; other civilians go to the host nation hospitals (was Y*, the same as amber)
    EPW_EWIA:           _mkCell('Y',  'Y'),
    VIP_HVT:            _mkCell('N',  'N'),
    CONTRACTORS:        _mkCell('N',  'N'),
  },
};

// ============================================================================
// V3.1 -- THEATER PRESETS
// ============================================================================

export const MROE_PRESETS = {
  OFFENSIVE: {
    key:              'OFFENSIVE',
    label:            'Offensive Operations',
    state:            'AMBER',
    matrix:           MROE_DEFAULT_MATRICES.AMBER,
    epw_volume:       'MODERATE',
    civilian_density: 'MINIMAL',
    epw_level:        'MODERATE',
    civilian_level:   'MINIMAL',
  },
  URBAN: {
    key:              'URBAN',
    label:            'Urban Operations',
    state:            'GREEN',
    matrix:           MROE_DEFAULT_MATRICES.GREEN,
    epw_volume:       'HIGH',
    civilian_density: 'HIGH',
    epw_level:        'HIGH',
    civilian_level:   'HIGH',
  },
  PEACEKEEPING: {
    key:              'PEACEKEEPING',
    label:            'Peacekeeping / Stabilization',
    state:            'GREEN',
    matrix:           MROE_DEFAULT_MATRICES.GREEN,
    epw_volume:       'LOW',
    civilian_density: 'MODERATE',
    epw_level:        'LOW',
    civilian_level:   'MODERATE',
  },
};

// ============================================================================
// EPW THREAT LEVELS (retained -- drives patient volume estimate)
// ============================================================================

export const EPW_THREAT_LEVELS = {
  NONE: {
    value:                    'NONE',
    label:                    'No EPW Expected',
    shortLabel:               'NONE',
    description:              'No enemy captures expected',
    icon:                     STATUS_ICONS.GREEN,
    color:                    'success',
    epw_per_1000_friendly_day: 0,
    wounded_epw_percentage:   0,
    medical_capacity_impact:  0,
    notes:                    'No EPW medical planning required.',
  },
  LOW: {
    value:                    'LOW',
    label:                    'Low EPW Volume',
    shortLabel:               'LOW',
    description:              'Occasional captures, small numbers',
    icon:                     STATUS_ICONS.BLUE,
    color:                    'info',
    epw_per_1000_friendly_day: 0.5,
    wounded_epw_percentage:   15,
    medical_capacity_impact:  0.02,
    holding_capacity_required: 10,
    medical_screening_required: true,
    notes:                    'Minimal impact. Standard EPW procedures.',
  },
  MODERATE: {
    value:                    'MODERATE',
    label:                    'Moderate EPW Volume',
    shortLabel:               'MOD',
    description:              'Regular captures during offensive ops',
    icon:                     STATUS_ICONS.YELLOW,
    color:                    'warning',
    epw_per_1000_friendly_day: 2.0,
    wounded_epw_percentage:   20,
    medical_capacity_impact:  0.08,
    holding_capacity_required: 50,
    medical_screening_required: true,
    dedicated_epw_medical:    true,
    ukraine_data: {
      applicable: true,
      notes:      'POW exchanges became significant medical planning factor',
    },
    notes: 'Plan for EPW medical section. Screening delays possible.',
  },
  HIGH: {
    value:                    'HIGH',
    label:                    'High EPW Volume',
    shortLabel:               'HIGH',
    description:              'Large-scale captures, unit surrenders',
    icon:                     STATUS_ICONS.YELLOW_WARNING,
    color:                    'error',
    epw_per_1000_friendly_day: 5.0,
    wounded_epw_percentage:   25,
    medical_capacity_impact:  0.15,
    holding_capacity_required: 200,
    medical_screening_required: true,
    dedicated_epw_medical:    true,
    epw_holding_facility:     true,
    notes:                    'Significant medical resources required. Request EPW medical augmentation.',
  },
  MASS_SURRENDER: {
    value:                    'MASS_SURRENDER',
    label:                    'Mass Surrender',
    shortLabel:               'MASS',
    description:              'Division/Corps level surrenders possible',
    icon:                     STATUS_ICONS.RED,
    color:                    'error',
    epw_per_1000_friendly_day: 20.0,
    wounded_epw_percentage:   30,
    medical_capacity_impact:  0.35,
    holding_capacity_required: 1000,
    medical_screening_required: true,
    dedicated_epw_medical:    true,
    epw_holding_facility:     true,
    epw_hospital_required:    true,
    notes:                    'MASCAL-level EPW medical surge. Strategic evacuation required.',
  },
};

// ============================================================================
// CIVILIAN CASUALTY SCENARIOS (retained -- drives patient volume estimate)
// ============================================================================

export const CIVILIAN_CASUALTY_LEVELS = {
  NONE: {
    value:                        'NONE',
    label:                        'No Civilian Casualties',
    shortLabel:                   'NONE',
    description:                  'Unpopulated area or evacuated',
    icon:                         STATUS_ICONS.GREEN,
    color:                        'success',
    civilian_population_density:  0,
    expected_civilian_casualties_day: 0,
    medical_capacity_impact:      0,
    notes:                        'No civilian medical responsibility.',
  },
  MINIMAL: {
    value:                        'MINIMAL',
    label:                        'Minimal Civilian Impact',
    shortLabel:                   'MIN',
    description:                  'Rural area, low population',
    icon:                         STATUS_ICONS.BLUE,
    color:                        'info',
    civilian_population_density:  'LOW',
    expected_civilian_casualties_day: 5,
    medical_capacity_impact:      0.03,
    civilian_treatment_obligation: 'EMERGENCY_ONLY',
    cmo_coordination:             false,
    notes:                        'Treat life-threatening only. Local hospitals available.',
  },
  MODERATE: {
    value:                        'MODERATE',
    label:                        'Moderate Civilian Area',
    shortLabel:                   'MOD',
    description:                  'Suburban/small town operations',
    icon:                         STATUS_ICONS.YELLOW,
    color:                        'warning',
    civilian_population_density:  'MEDIUM',
    expected_civilian_casualties_day: 25,
    medical_capacity_impact:      0.10,
    civilian_treatment_obligation: 'ALL_URGENT',
    cmo_coordination:             true,
    ngos_present:                 true,
    notes:                        'CMO coordination required. Plan civilian triage separate from military.',
  },
  HIGH: {
    value:                        'HIGH',
    label:                        'High Civilian Density',
    shortLabel:                   'HIGH',
    description:                  'Urban operations, dense population',
    icon:                         STATUS_ICONS.YELLOW_WARNING,
    color:                        'error',
    civilian_population_density:  'HIGH',
    expected_civilian_casualties_day: 100,
    medical_capacity_impact:      0.25,
    civilian_treatment_obligation: 'COMPREHENSIVE',
    cmo_coordination:             true,
    ngos_present:                 true,
    civilian_hospital_augmentation: true,
    ukraine_data: {
      applicable: true,
      examples:   ['Kharkiv', 'Kherson'],
      notes:      'Urban shelling created massive civilian casualty loads',
    },
    notes: 'Major civilian medical responsibility. May exceed military capacity.',
  },
  URBAN_COMBAT: {
    value:                        'URBAN_COMBAT',
    label:                        'Urban Combat/Siege',
    shortLabel:                   'URBAN',
    description:                  'Dense urban combat, no evacuation',
    icon:                         STATUS_ICONS.RED,
    color:                        'error',
    civilian_population_density:  'VERY_HIGH',
    expected_civilian_casualties_day: 500,
    medical_capacity_impact:      0.50,
    civilian_treatment_obligation: 'COMPREHENSIVE',
    cmo_coordination:             true,
    ngos_present:                 true,
    humanitarian_crisis:          true,
    ukraine_data: {
      applicable: true,
      examples:   ['Mariupol siege', 'Bakhmut'],
      notes:      'Civilian casualties overwhelmed all medical capacity',
    },
    notes: 'Humanitarian crisis likely. Strategic medical assets required.',
  },
};

// ============================================================================
// TREATMENT OBLIGATIONS
// ============================================================================

export const TREATMENT_OBLIGATIONS = {
  EMERGENCY_ONLY: {
    value:                    'EMERGENCY_ONLY',
    label:                    'Emergency Only',
    description:              'Life-threatening injuries only',
    icon:                     MEDICAL_ICONS.EMERGENCY,
    treatment_scope:          ['LIFE_THREATENING'],
    holding_time_hours:       4,
    evacuation_responsibility: 'TRANSFER_CIVILIAN',
    capacity_allocation:      0.05,
  },
  ALL_URGENT: {
    value:                    'ALL_URGENT',
    label:                    'All Urgent Cases',
    description:              'Urgent and emergency cases',
    icon:                     MEDICAL_ICONS.FIRST_AID,
    treatment_scope:          ['LIFE_THREATENING', 'URGENT'],
    holding_time_hours:       24,
    evacuation_responsibility: 'COORDINATE_WITH_HNS',
    capacity_allocation:      0.10,
  },
  COMPREHENSIVE: {
    value:                    'COMPREHENSIVE',
    label:                    'Comprehensive Care',
    description:              'Full care until civilian system available',
    icon:                     MEDICAL_ICONS.HOSPITAL,
    treatment_scope:          ['LIFE_THREATENING', 'URGENT', 'ROUTINE'],
    holding_time_hours:       72,
    evacuation_responsibility: 'MILITARY_IF_NEEDED',
    capacity_allocation:      0.20,
  },
};

// ============================================================================
// V4-CAL-001: CIVILIAN TRIAGE PROFILES
// Urban vs Rural triage severity distribution for civilian patient stream.
// Urban:  30% T1 (surgical/immediate), 40% T2 (ward/delayed), 30% T3 (minimal/HNS)
// Rural:  10% T1 (surgical/immediate), 30% T2 (ward/delayed), 60% T3 (minimal/HNS)
// Source: Ukraine 2022-2024 data (Kharkiv urban, rural oblast comparisons)
// ============================================================================

export const CIVILIAN_TRIAGE_PROFILES = {
  URBAN: {
    label:   'Urban (high severity)',
    t1_frac: 0.30,
    t2_frac: 0.40,
    t3_frac: 0.30,
  },
  RURAL: {
    label:   'Rural (lower severity)',
    t1_frac: 0.10,
    t2_frac: 0.30,
    t3_frac: 0.60,
  },
};

// Maps civilian_density / civilian_level selector value to triage profile key.
const _CIV_DENSITY_TO_TRIAGE = {
  NONE:         null,
  MINIMAL:      'RURAL',
  MODERATE:     'RURAL',
  HIGH:         'URBAN',
  URBAN_COMBAT: 'URBAN',
};

// EPW hold longer than friendly due to GC III Art.12 repatriation requirement (no RTD).
// Used in both getMRoEModifiers (epw_stream) and casualtyCalculator (_EPW_BED_MULT).
export const EPW_BED_MULT = 1.4;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate EPW medical requirements.
 * V3.1: reads epw_volume first, falls back to epw_level for backward compat.
 */
export function calculateEPWRequirements(mroeConfig, forceStrength) {
  const epw_level  = mroeConfig.epw_volume || mroeConfig.epw_level || 'NONE';
  const epwData    = EPW_THREAT_LEVELS[epw_level] || EPW_THREAT_LEVELS.NONE;
  // 2026-10-21: not rounded - rounding made the LOW level (0.4 wounded prisoners a day at 5000 troops) zero; displays round to one decimal
  const expectedEPWPerDay  = forceStrength / 1000 * epwData.epw_per_1000_friendly_day;
  const woundedEPWPerDay   = expectedEPWPerDay * (epwData.wounded_epw_percentage / 100);
  return {
    total_epw_per_day:          expectedEPWPerDay,
    wounded_epw_per_day:        woundedEPWPerDay,
    holding_capacity_needed:    epwData.holding_capacity_required || 0,
    medical_capacity_impact:    epwData.medical_capacity_impact,
    dedicated_medical_required: epwData.dedicated_epw_medical || false,
    hospital_required:          epwData.epw_hospital_required || false,
    icon:                       epwData.icon,
  };
}

/**
 * Calculate civilian casualty requirements.
 * V3.1: reads civilian_density first, falls back to civilian_level for backward compat.
 */
export function calculateCivilianRequirements(mroeConfig) {
  const civilian_level = mroeConfig.civilian_density || mroeConfig.civilian_level || 'NONE';
  const civData    = CIVILIAN_CASUALTY_LEVELS[civilian_level] || CIVILIAN_CASUALTY_LEVELS.NONE;
  const obligation = TREATMENT_OBLIGATIONS[civData.civilian_treatment_obligation] ||
                     TREATMENT_OBLIGATIONS.EMERGENCY_ONLY;
  return {
    expected_casualties_per_day: civData.expected_civilian_casualties_day,
    capacity_impact:             civData.medical_capacity_impact,
    treatment_scope:             obligation.treatment_scope,
    holding_time:                obligation.holding_time_hours,
    cmo_required:                civData.cmo_coordination || false,
    humanitarian_crisis:         civData.humanitarian_crisis || false,
    icon:                        civData.icon,
  };
}

// Civilian patient category keys in the matrix (used for capacity fraction calculation)
const _CIVILIAN_MATRIX_KEYS = [
  'HN_PARTNERED',
  'HN_UNPARTNERED',
  'CIVCAS_OWN',
  'CIVCAS_OTHER',
  'CONTRACTORS',
];

/**
 * Get combined MRoE modifiers for calculator.
 *
 * V3.1: matrix-aware. If mroeConfig.state is set (new shape), civilian capacity impact
 * is scaled by the fraction of civilian matrix rows that have Y/Y* in the LLE column.
 *
 * Output shape is IDENTICAL to v2.x -- no calculator changes required.
 * Calculator reads: total_capacity_impact, epw_casualties_day, civilian_casualties_day,
 * epw_dedicated_medical, civilian_cmo_required, humanitarian_crisis, warnings, icon.
 */
export function getMRoEModifiers(mroeConfig, forceStrength = 10000) {
  const { enabled = false } = mroeConfig;

  if (!enabled) {
    return {
      capacity_impact:          0,
      epw_casualties_day:       0,
      civilian_casualties_day:  0,
      icon:                     STATUS_ICONS.GREEN,
      warnings:                 [],
    };
  }

  const epwReqs = calculateEPWRequirements(mroeConfig, forceStrength);
  const civReqs = calculateCivilianRequirements(mroeConfig);

  // V3.1: if matrix present, scale civilian capacity by fraction of active LLE cells
  let civFraction = 1.0;
  if (mroeConfig.matrix) {
    const activeCivCount = _CIVILIAN_MATRIX_KEYS.filter(k => {
      const cell = mroeConfig.matrix[k];
      return cell && (cell.lle === 'Y' || cell.lle === 'Y*');
    }).length;
    civFraction = activeCivCount / _CIVILIAN_MATRIX_KEYS.length;
  }

  const adjustedCivImpact   = civReqs.capacity_impact * civFraction;
  const totalCapacityImpact = epwReqs.medical_capacity_impact + adjustedCivImpact;

  const warnings = [];

  if (totalCapacityImpact > 0.3) {
    warnings.push({
      severity: 'error',
      icon:     STATUS_ICONS.RED,
      category: 'CAPACITY',
      message:  `EPW and civilian casualties will consume ${Math.round(totalCapacityImpact * 100)}% of medical capacity`,
    });
  } else if (totalCapacityImpact > 0.15) {
    warnings.push({
      severity: 'warning',
      icon:     STATUS_ICONS.YELLOW_WARNING,
      category: 'CAPACITY',
      message:  `Non-military casualties will consume ${Math.round(totalCapacityImpact * 100)}% of capacity`,
    });
  }

  if (epwReqs.hospital_required) {
    warnings.push({
      severity: 'warning',
      icon:     STATUS_ICONS.YELLOW,
      category: 'EPW',
      message:  'EPW volume requires dedicated EPW hospital capability',
    });
  }

  if (civReqs.humanitarian_crisis) {
    warnings.push({
      severity: 'error',
      icon:     STATUS_ICONS.RED,
      category: 'HUMANITARIAN',
      message:  'Humanitarian crisis conditions - civilian medical needs may overwhelm military capacity',
    });
  }

  if (civReqs.cmo_required) {
    warnings.push({
      severity: 'info',
      icon:     STATUS_ICONS.BLUE,
      category: 'CMO',
      message:  'Civil-Military Operations coordination required',
    });
  }

  // V3.1: matrix-specific legal warnings
  if (mroeConfig.matrix) {
    const civcasOwnLle = mroeConfig.matrix.CIVCAS_OWN && mroeConfig.matrix.CIVCAS_OWN.lle;
    if (civcasOwnLle === 'N') {
      warnings.push({
        severity: 'error',
        icon:     STATUS_ICONS.RED,
        category: 'LEGAL',
        message:  'CIVCAS (own forces) LLE set to N. GC IV Art.16 -- LEGAD authorization required. Printed in all reports.',
      });
    }
    // Under GREEN: full exclusion (both N) of any civilian category
    if (mroeConfig.state === 'GREEN') {
      _CIVILIAN_MATRIX_KEYS.forEach(k => {
        const cell = mroeConfig.matrix[k];
        if (cell && cell.lle === 'N' && cell.nonLle === 'N') {
          const cat = PATIENT_CATEGORIES.find(c => c.key === k);
          warnings.push({
            severity: 'warning',
            icon:     STATUS_ICONS.YELLOW_WARNING,
            category: 'LEGAL',
            message:  `${cat ? cat.label : k}: fully excluded under MRoE GREEN. Significant deviation from standard obligations.`,
          });
        }
      });
    }
  }

  let overallIcon = STATUS_ICONS.GREEN;
  if (warnings.some(w => w.severity === 'error')) {
    overallIcon = STATUS_ICONS.RED;
  } else if (warnings.some(w => w.severity === 'warning')) {
    overallIcon = STATUS_ICONS.YELLOW;
  }

  // Resolve field names for backward compat output
  const resolvedEpwLevel = mroeConfig.epw_volume || mroeConfig.epw_level || 'NONE';
  const resolvedCivLevel = mroeConfig.civilian_density || mroeConfig.civilian_level || 'NONE';

  // V4-CAL-001: Civilian triage profile (urban vs rural severity split)
  const civTriageProfileKey = _CIV_DENSITY_TO_TRIAGE[resolvedCivLevel] || null;
  const civTriageProfile    = civTriageProfileKey ? CIVILIAN_TRIAGE_PROFILES[civTriageProfileKey] : null;
  const civDaily            = civReqs.expected_casualties_per_day || 0;
  const epwDaily            = epwReqs.wounded_epw_per_day || 0;

  return {
    // V2.x compatible output -- calculator reads these fields unchanged
    epw_level:                resolvedEpwLevel,
    civilian_level:           resolvedCivLevel,
    enabled,
    total_capacity_impact:    Math.round(totalCapacityImpact * 100) / 100,
    epw_capacity_impact:      epwReqs.medical_capacity_impact,
    civilian_capacity_impact: adjustedCivImpact,
    epw_casualties_day:       epwDaily,
    civilian_casualties_day:  civDaily,
    epw_holding_capacity:     epwReqs.holding_capacity_needed,
    epw_dedicated_medical:    epwReqs.dedicated_medical_required,
    civilian_cmo_required:    civReqs.cmo_required,
    humanitarian_crisis:      civReqs.humanitarian_crisis || false,
    icon:                     overallIcon,
    warnings,
    // V3.1 additions (not read by calculator -- for Tab5/Tab6 display only)
    mroe_state:               mroeConfig.state || null,
    civ_active_fraction:      Math.round(civFraction * 100),
    // V4-CAL-001: Two-stream breakdown (Tab5/Tab6 display; not read by legacy calculator path)
    epw_stream: {
      daily_patients:             epwDaily,
      section_beds_daily:         Math.ceil(epwDaily * EPW_BED_MULT),
      collection_hold_days:       1,
      mp_escort_required:         epwDaily > 0,
      dedicated_section_required: epwReqs.dedicated_medical_required,
      additive:                   true,   // additive beds -- does NOT compete with military surgical
    },
    civilian_stream: {
      daily_patients:       civDaily,
      triage_profile:       civTriageProfileKey,
      t1_surgical_daily:    civTriageProfile ? Math.round(civDaily * civTriageProfile.t1_frac) : 0,
      t2_ward_daily:        civTriageProfile ? Math.round(civDaily * civTriageProfile.t2_frac) : 0,
      t3_hns_daily:         civTriageProfile ? Math.round(civDaily * civTriageProfile.t3_frac) : 0,
      competes_with_military: civDaily > 0,
      civ_active_fraction:  Math.round(civFraction * 100),
    },
  };
}

/**
 * Validate MRoE configuration.
 * V3.1: reads epw_volume/civilian_density first, falls back to epw_level/civilian_level.
 */
export function validateMRoEConfig(mroeConfig) {
  const errors   = [];
  const warnings = [];

  const resolvedEpwLevel = mroeConfig.epw_volume || mroeConfig.epw_level;
  const resolvedCivLevel = mroeConfig.civilian_density || mroeConfig.civilian_level;
  const epwData = EPW_THREAT_LEVELS[resolvedEpwLevel];
  const civData = CIVILIAN_CASUALTY_LEVELS[resolvedCivLevel];

  if (!epwData) errors.push('Invalid EPW volume level selected');
  if (!civData) errors.push('Invalid civilian density level selected');

  if (epwData && civData &&
      epwData.value === 'MASS_SURRENDER' && civData.value === 'URBAN_COMBAT') {
    warnings.push('Mass surrender + urban combat = extreme medical burden. Consider phased operations.');
  }

  return {
    errors,
    warnings,
    valid:   errors.length === 0,
    isValid: errors.length === 0,
    icon:    errors.length > 0   ? STATUS_ICONS.RED    :
             warnings.length > 0 ? STATUS_ICONS.YELLOW :
                                   STATUS_ICONS.GREEN,
  };
}

export function getEPWLevelOptions() {
  return Object.values(EPW_THREAT_LEVELS).map(level => ({
    value:           level.value,
    label:           level.label,
    icon:            level.icon,
    capacity_impact: level.medical_capacity_impact,
  }));
}

export function getCivilianLevelOptions() {
  return Object.values(CIVILIAN_CASUALTY_LEVELS).map(level => ({
    value:           level.value,
    label:           level.label,
    icon:            level.icon,
    capacity_impact: level.medical_capacity_impact,
  }));
}

// ============================================================================
// DEFAULT CONFIGURATIONS (updated V3.1 -- all old fields retained)
// ============================================================================

export const DEFAULT_MROE_CONFIG = {
  enabled:          false,
  // V3.1 fields
  state:            null,
  matrix:           null,
  epw_volume:       'NONE',
  civilian_density: 'NONE',
  // Legacy fields -- retained for backward compat with pre-V3.1 saved scenarios
  epw_level:        'NONE',
  civilian_level:   'NONE',
  // 2026-10-21 (R2-08): surgical cases per day of the other patient categories (fixed numbers, not rates per 1,000)
  hn_partnered_cases_day:   0,
  hn_unpartnered_cases_day: 0,
  vip_cases_day:            0,
  contractor_cases_day:     0,
};

export const OFFENSIVE_MROE_CONFIG = {
  enabled:          true,
  state:            'AMBER',
  matrix:           MROE_DEFAULT_MATRICES.AMBER,
  epw_volume:       'MODERATE',
  civilian_density: 'MINIMAL',
  epw_level:        'MODERATE',
  civilian_level:   'MINIMAL',
};

export const URBAN_MROE_CONFIG = {
  enabled:          true,
  state:            'GREEN',
  matrix:           MROE_DEFAULT_MATRICES.GREEN,
  epw_volume:       'HIGH',
  civilian_density: 'HIGH',
  epw_level:        'HIGH',
  civilian_level:   'HIGH',
};

export const PEACEKEEPING_MROE_CONFIG = {
  enabled:          true,
  state:            'GREEN',
  matrix:           MROE_DEFAULT_MATRICES.GREEN,
  epw_volume:       'LOW',
  civilian_density: 'MODERATE',
  epw_level:        'LOW',
  civilian_level:   'MODERATE',
};

// ============================================================================
// EDUCATIONAL CONTENT (retained)
// ============================================================================

export const MROE_EDUCATIONAL = {
  key_principles: [
    'Geneva Conventions require equal medical treatment for EPW',
    'EPW medical screening required before internment',
    'Civilian casualties create legal and moral obligations',
    'CMO coordination essential in populated areas',
  ],
  ukraine_lessons: [
    'POW exchanges created complex medical planning requirements',
    'Urban combat in Mariupol, Bakhmut overwhelmed medical capacity',
    'Civilian casualty loads often exceeded military casualties',
    'NGO/ICRC coordination critical for civilian care',
  ],
  planning_factors: [
    'EPW wounded rate: 15-30% depending on combat intensity',
    'Urban combat civilian casualties: 100-500 per day possible',
    'CMO cell recommended when civilian impact > 0.10',
    'Humanitarian crisis threshold: civilian impact > 0.25',
  ],
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const mroeConfigConstants = {
  // V3.1 additions
  MROE_STATES,
  PATIENT_CATEGORIES,
  MROE_DEFAULT_MATRICES,
  MROE_Y_STAR_CONDITIONS,
  MROE_PRESETS,
  // V4-CAL-001 additions
  CIVILIAN_TRIAGE_PROFILES,
  EPW_BED_MULT,
  // Retained from v2.x
  EPW_THREAT_LEVELS,
  CIVILIAN_CASUALTY_LEVELS,
  TREATMENT_OBLIGATIONS,
  calculateEPWRequirements,
  calculateCivilianRequirements,
  getMRoEModifiers,
  validateMRoEConfig,
  getEPWLevelOptions,
  getCivilianLevelOptions,
  DEFAULT_MROE_CONFIG,
  OFFENSIVE_MROE_CONFIG,
  URBAN_MROE_CONFIG,
  PEACEKEEPING_MROE_CONFIG,
  MROE_EDUCATIONAL,
};

export default mroeConfigConstants;
